import React, { Fragment, useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import { themeGet } from 'styled-system';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { Box, NakedButton, Hide } from 'roo-ui/components';
import { Label, LabelText } from 'components/Label';
import InputWithIcon from 'components/InputWithIcon';
import ResponsiveModal from 'components/ResponsiveModal';
import Dropdown from 'components/Dropdown';
import Occupant from './Occupant';
import { mediaQuery } from 'lib/styledSystem';

const MAX_OCCUPANT_COUNT = 9;

const Root = styled(Box)`
  position: relative;
  user-select: none;
`;

const InputWrapper = styled(Box)`
  ${(props) =>
    props.isOpen &&
    css`
      display: none;

      ${mediaQuery.minWidth.sm(props)} {
        display: block;
      }
    `};
`;

const OccupantInput = styled(InputWithIcon)`
  &:focus {
    border-color: ${themeGet('colors.blue')};
  }
`;

const Actions = styled(Box)`
  position: fixed;
  padding: ${themeGet('space.4')};
  text-align: right;
  left: 0;
  right: 0;
  bottom: 0;
  border: none;

  ${mediaQuery.minWidth.sm} {
    position: relative;
    left: auto;
    right: auto;
    bottom: auto;
    border: 1px solid ${themeGet('colors.greys.alto')};
  }
`;

const OccupantPicker = ({ occupants, labelOptions, updateQuery, viewThreshold, verboseInMobile }) => {
  const [adults, setAdults] = useState(occupants.adults ?? 0);
  const [children, setChildren] = useState(occupants.children ?? 0);
  const [infants, setInfants] = useState(occupants.infants ?? 0);

  // update internal state based on incoming props, most of the time it will already be aligned, but a history state
  // change could get the the states out of sync.
  useEffect(() => {
    setAdults(occupants.adults ?? 0);
    setChildren(occupants.children ?? 0);
    setInfants(occupants.infants ?? 0);
  }, [occupants]);

  const occupantCount = useMemo(() => adults + children + infants, [adults, children, infants]);
  const summaryText = useMemo(() => `${occupantCount} ${pluralize('guest', occupantCount)}`, [occupantCount]);

  const isDirty = useMemo(() => adults !== occupants.adults || children !== occupants.children || infants !== occupants.infants, [
    adults,
    children,
    infants,
    occupants,
  ]);

  const apply = () => {
    if (isDirty) {
      updateQuery({ adults, children, infants });
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      setAdults(occupants.adults ?? 0);
      setChildren(occupants.children ?? 0);
      setInfants(occupants.infants ?? 0);
    }
  };

  const incrementAdults = useCallback(() => setAdults(adults + 1), [adults]);
  const decrementAdults = useCallback(() => setAdults(adults - 1), [adults]);

  const incrementChildren = useCallback(() => setChildren(children + 1), [children]);
  const decrementChildren = useCallback(() => setChildren(children - 1), [children]);

  const incrementInfants = useCallback(() => setInfants(infants + 1), [infants]);
  const decrementInfants = useCallback(() => setInfants(infants - 1), [infants]);

  return (
    <ResponsiveModal title="Select guests" onSubmit={apply} onBlur={apply} onCancel={handleCancel}>
      {({ isOpen, openModal, submitModal, cancelModal }) => (
        <Fragment>
          <Root data-testid="occupant-picker">
            <InputWrapper isOpen={isOpen}>
              <Hide xs>
                <Label position="relative">
                  <LabelText {...labelOptions} color="greys.charcoal" fontWeight="normal" fontSize="sm">
                    Guests
                  </LabelText>
                  <InputWithIcon
                    data-testid="occupant-picker-input"
                    borderRadius="default"
                    icon="person"
                    value={summaryText}
                    onFocus={openModal}
                    isHighlighted={isOpen}
                    border={2}
                    readOnly
                    cursor="pointer"
                  />
                </Label>
              </Hide>
              <Hide sm md lg>
                <Label>
                  <LabelText {...labelOptions} hidden={[true, false]} color="greys.charcoal" fontWeight="normal">
                    Guests 2
                  </LabelText>
                  <OccupantInput
                    borderRadius="default"
                    icon="person"
                    value={verboseInMobile ? summaryText : occupantCount}
                    onClick={openModal}
                    onKeyUp={({ keyCode }) => {
                      if (keyCode === 13) openModal();
                    }}
                    isHighlighted={isOpen}
                    readOnly
                    border={[1, 2, 2]}
                    cursor="pointer"
                  />
                </Label>
              </Hide>
            </InputWrapper>
            {isOpen && (
              <Dropdown
                anchorX="right"
                width={['100%', '194px']}
                viewThreshold={viewThreshold}
                skipToContent="#main-content"
                cancelModal={cancelModal}
              >
                <Box>
                  <Occupant
                    data-testid="occupant-picker-adults"
                    label="Adults"
                    count={adults}
                    increment={incrementAdults}
                    decrement={decrementAdults}
                    min={1}
                    max={MAX_OCCUPANT_COUNT}
                  />
                  <Occupant
                    data-testid="occupant-picker-children"
                    label="Children"
                    ages="2 - 12yrs"
                    count={children}
                    increment={incrementChildren}
                    decrement={decrementChildren}
                    min={0}
                    max={MAX_OCCUPANT_COUNT}
                  />
                  <Occupant
                    data-testid="occupant-picker-infants"
                    label="Infants"
                    ages="< 2yrs"
                    count={infants}
                    increment={incrementInfants}
                    decrement={decrementInfants}
                    min={0}
                    max={MAX_OCCUPANT_COUNT}
                  />
                </Box>
                <Actions>
                  <Hide xs>
                    <NakedButton color="colors.ui.link" data-testid="done-button" onClick={submitModal}>
                      Done
                    </NakedButton>
                  </Hide>
                </Actions>
              </Dropdown>
            )}
          </Root>
        </Fragment>
      )}
    </ResponsiveModal>
  );
};

OccupantPicker.propTypes = {
  occupants: PropTypes.shape({
    adults: PropTypes.number,
    children: PropTypes.number,
    infants: PropTypes.number,
  }),
  labelOptions: PropTypes.object,
  updateQuery: PropTypes.func.isRequired,
  viewThreshold: PropTypes.number,
  verboseInMobile: PropTypes.bool,
};

OccupantPicker.defaultProps = {
  occupants: {
    adults: 0,
    children: 0,
    infants: 0,
  },
  labelOptions: {},
  viewThreshold: 0.5,
  verboseInMobile: false,
};

export default OccupantPicker;
